import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Tabs, Tab, Table, Badge, Button, Modal, Form, Alert } from "react-bootstrap";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import { downloadAuthedPdf } from "../../../utils/apiDownload";
import { exportListExcel, printListPdf } from "../../../utils/listExport";

const APP_COLS = [
  { key: (r) => r.application_no || "-", label: "Application ID" },
  { key: "job_title", label: "Job" },
  { key: "org_name", label: "Company" },
  { key: "job_location", label: "Location" },
  { key: "status", label: "Status" },
];

/**
 * Student portal > Jobs (Placement). The logged-in student browses live
 * vacancies, applies (only if course-complete), and tracks their applications
 * + status. Uses the existing student-portal look. Apply itself hits the public
 * placement endpoints; status is read from the student-scoped endpoint.
 */
const FILE_BASE = "https://nsvec.com/api/public/";
const sBadge = (s) => {
  const v = { selected: "success", joined: "success", rejected: "danger", shortlisted: "info", interview: "primary", pending: "warning" }[s] || "secondary";
  return <Badge variant={v}>{s}</Badge>;
};

export default function Jobs() {
  const dispatch = useDispatch();
  const stoken = () => localStorage.getItem("studentToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [eligible, setEligible] = useState(null); // {eligible, roll_number}
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [search, setSearch] = useState("");
  const [apply, setApply] = useState(null); // job being applied to
  const [form, setForm] = useState({});
  const [resume, setResume] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadJobs = async () => {
    try {
      const res = await dispatch(getDataAPI(`placement/jobs?search=${encodeURIComponent(search)}`, stoken()));
      setJobs((res.data.data && res.data.data.data) || []);
    } catch (e) { /* public, ignore */ }
  };
  const loadApps = async () => {
    try {
      const res = await dispatch(getDataAPI("student/placement/my-applications", stoken()));
      setApps(res.data.data || []);
    } catch (e) { /* ignore */ }
  };
  const loadEligibility = async () => {
    try {
      const res = await dispatch(getDataAPI("student/placement/eligibility", stoken()));
      setEligible(res.data);
    } catch (e) { setEligible({ eligible: false }); }
  };

  useEffect(() => {
    loadEligibility();
    loadJobs();
    loadApps();
    // eslint-disable-next-line
  }, []);

  const openApply = (job) => {
    if (!eligible || !eligible.eligible) {
      alert({ error: "You can apply only after completing your course." });
      return;
    }
    setForm({ mobile: "", email: "", last_qualification: "", experience: "", pref_state: "", pref_city: "" });
    setResume(null); setPhoto(null);
    setApply(job);
  };

  const submitApply = async () => {
    if (!form.mobile || !/^\d{10}$/.test(form.mobile)) {
      alert({ error: "Enter a valid 10-digit mobile number." });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("job_id", apply.id);
      fd.append("roll_number", eligible.roll_number);
      Object.keys(form).forEach((k) => fd.append(k, form[k] || ""));
      if (resume) fd.append("resume", resume);
      if (photo) fd.append("photo", photo);
      const res = await dispatch(postDataAPI("placement/apply", fd, stoken()));
      alert({ success: res.data.message || "Applied successfully." });
      setApply(null);
      loadApps();
    } catch (e) {
      alert({ error: (e.response && e.response.data && e.response.data.message) || "Could not apply." });
    }
    setBusy(false);
  };

  const fld = (k, label, type = "text") => (
    <Form.Group>
      <Form.Label className="small mb-1">{label}</Form.Label>
      <Form.Control type={type} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
    </Form.Group>
  );

  return (
    <div className="container-fluid py-4">
      <h4 className="mb-3">Jobs &amp; Placement</h4>

      {eligible && !eligible.eligible && (
        <Alert variant="warning">
          Job application opens after you complete your course. You can still browse live jobs below.
        </Alert>
      )}

      <Tabs defaultActiveKey="browse" id="jobs-tabs" className="mb-3">
        <Tab eventKey="browse" title="Browse Jobs">
          <div className="d-flex mb-3" style={{ gap: 10, maxWidth: 420 }}>
            <Form.Control value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadJobs()} placeholder="Search title / sector" />
            <Button variant="primary" onClick={loadJobs}>Search</Button>
          </div>
          <div className="row">
            {jobs.length === 0 && <div className="col-12 text-muted">No live jobs right now.</div>}
            {jobs.map((j) => (
              <div className="col-md-6 col-lg-4 mb-3" key={j.id}>
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <h6 className="font-weight-bold mb-1">{j.title}</h6>
                    <div className="text-muted small mb-2">{j.org_name}{j.company_city ? `, ${j.company_city}` : ""}</div>
                    <div className="small mb-1">{j.job_city}{j.job_state ? `, ${j.job_state}` : ""}</div>
                    {j.salary && <div className="small mb-1"><strong>Salary:</strong> {j.salary}</div>}
                    {j.qualification && <div className="small mb-1"><strong>Qualification:</strong> {j.qualification}</div>}
                    {j.sector_trade && <div className="small mb-2"><Badge variant="light">{j.sector_trade}</Badge></div>}
                    <Button size="sm" variant="primary" onClick={() => openApply(j)}>Apply</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Tab>

        <Tab eventKey="mine" title={`My Applications (${apps.length})`}>
          <div className="d-flex justify-content-end mb-2" style={{ gap: 8 }}>
            <Button variant="outline-success" size="sm" onClick={() => exportListExcel(APP_COLS, apps, "my-applications")}>Excel</Button>
            <Button variant="outline-secondary" size="sm" onClick={() => printListPdf("My Applications", APP_COLS, apps)}>PDF</Button>
          </div>
          <Table bordered hover responsive size="sm">
            <thead className="thead-light">
              <tr><th>Application ID</th><th>Job</th><th>Company</th><th>Location</th><th>Status</th><th>Offer</th><th>Form</th></tr>
            </thead>
            <tbody>
              {apps.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-3">You have not applied to any job yet.</td></tr>}
              {apps.map((a) => (
                <tr key={a.id}>
                  <td>{a.application_no || "-"}</td>
                  <td>{a.job_title}</td>
                  <td>{a.org_name}</td>
                  <td>{a.job_location}</td>
                  <td>{sBadge(a.status)}{a.status_reason ? <div className="small text-muted">{a.status_reason}</div> : null}</td>
                  <td>{a.record && a.record.offer_letter ? <a href={FILE_BASE + a.record.offer_letter} target="_blank" rel="noopener noreferrer">Offer</a> : "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() =>
                        downloadAuthedPdf(
                          "placement/application-pdf",
                          "",
                          (a.application_no || "application-" + a.id) + ".pdf",
                          "post",
                          { application_id: a.id, roll_number: (eligible && eligible.roll_number) || a.roll_number }
                        ).catch(() => alert({ error: "Could not download the application form." }))
                      }
                    >
                      PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>
      </Tabs>

      <Modal show={!!apply} onHide={() => setApply(null)}>
        <Modal.Header closeButton><Modal.Title>Apply{apply ? ` — ${apply.title}` : ""}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="small text-muted">Applying as roll no. <strong>{eligible && eligible.roll_number}</strong>. Fill the details below.</p>
          <div className="row">
            <div className="col-md-6">{fld("mobile", "Mobile (10 digit) *")}</div>
            <div className="col-md-6">{fld("email", "Email", "email")}</div>
            <div className="col-md-6">{fld("last_qualification", "Last Qualification")}</div>
            <div className="col-md-6">{fld("experience", "Experience")}</div>
            <div className="col-md-6">{fld("pref_state", "Preferred State")}</div>
            <div className="col-md-6">{fld("pref_city", "Preferred City")}</div>
            <div className="col-md-6">
              <Form.Group><Form.Label className="small mb-1">Resume / CV</Form.Label>
                <Form.Control type="file" onChange={(e) => setResume(e.target.files[0])} /></Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group><Form.Label className="small mb-1">Photo</Form.Label>
                <Form.Control type="file" onChange={(e) => setPhoto(e.target.files[0])} /></Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setApply(null)}>Cancel</Button>
          <Button variant="primary" onClick={submitApply} disabled={busy}>Submit Application</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
